import { useCallback, useEffect, useRef, useState } from 'react'
import { extractRetryAfter } from '@/api/extractRetryAfter'

interface RateLimitState {
  /** Seconds remaining until retry is allowed; null when not rate-limited. */
  secondsLeft: number | null
  /** Human-readable error message to display, or null. */
  message: string | null
  /** Call when a 429 error is received to start the countdown. */
  handleRateLimit: (error: unknown) => boolean
  /** Reset countdown state (e.g. when form is cleared). */
  reset: () => void
}

/**
 * Manages a countdown timer for 429 Too Many Requests responses.
 * Returns `secondsLeft` (null when not rate-limited), `message`, and helpers.
 *
 * Usage:
 *   const { secondsLeft, message, handleRateLimit } = useRateLimitCountdown()
 *   // in catch: if (handleRateLimit(err)) return   // stops further handling
 */
export function useRateLimitCountdown(): RateLimitState {
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const handleRateLimit = useCallback(
    (error: unknown): boolean => {
      const status = (error as { response?: { status?: number } })?.response?.status
      if (status !== 429) return false

      const seconds = extractRetryAfter(error) ?? 60
      setSecondsLeft(seconds)

      clearTimer()
      timerRef.current = setInterval(() => {
        setSecondsLeft((prev) => {
          if (prev === null || prev <= 1) {
            clearTimer()
            return null
          }
          return prev - 1
        })
      }, 1000)

      return true
    },
    [clearTimer]
  )

  const reset = useCallback(() => {
    clearTimer()
    setSecondsLeft(null)
  }, [clearTimer])

  // Clean up on unmount
  useEffect(() => () => clearTimer(), [clearTimer])

  const message =
    secondsLeft !== null ? `Too many attempts. Please wait ${secondsLeft}s and try again.` : null

  return { secondsLeft, message, handleRateLimit, reset }
}
